/**
 * useUserCycleRoleContext
 *
 * Reactive wrapper around resolveUserCycleRoles().
 * Returns the current user's job context within a Cycle.
 *
 * Surface routing derives exclusively from primary RBAC, Catchball loop state,
 * and session participation. cycle_participants rows are no longer consulted
 * for surface selection — they serve as a display roster only.
 *
 * Usage:
 *   const ctx = useUserCycleRoleContext({ cycleId, activeLoopId });
 *   if (ctx.isAdmin) return <FullAdminView />;
 *   return <CycleMyWorkPanel context={ctx} />;
 */

import { useMemo } from 'react';
import { useShallow } from 'zustand/shallow';
import { useDegapProcessStore } from '../../stores/prioritization/degapProcessStore';
import { useSessionStore } from '../../stores/prioritization/sessionStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useCatchballLoopStore } from '../../stores/prioritization/catchballLoopStore';
import { useOrgAwareCatchball } from './useOrgAwareCatchball';
import {
  resolveUserCycleRoles,
  type UserCycleRoleContext,
} from '../../lib/cycleRoleContext';

interface UseUserCycleRoleContextOptions {
  cycleId: string | null | undefined;
  /** The active loop ID to resolve ball holder / decision authority status. */
  activeLoopId?: string | null;
}

/**
 * Returns a stable UserCycleRoleContext for the current user + cycle.
 * Re-computes when cycle, sessions, or org-aware Catchball state changes.
 */
export function useUserCycleRoleContext(
  options: UseUserCycleRoleContextOptions,
): UserCycleRoleContext {
  const { cycleId, activeLoopId } = options;

  const userId = useUserStore((s) => s.userId);
  const cycle = useDegapProcessStore((s) => (cycleId ? s.getCycle(cycleId) : undefined));
  const workspaceId = useDegapProcessStore((s) => {
    if (!cycleId) return null;
    const c = s.getCycle(cycleId);
    return c?.workspaceId ?? null;
  });

  const cycleSessions = useSessionStore(
    useShallow((s) => (cycleId ? s.getSessionsForCycle(cycleId) : [])),
  );

  const activeLoop = useCatchballLoopStore((s) =>
    activeLoopId ? s.getLoop(activeLoopId) ?? null : null,
  );

  const orgAware = useOrgAwareCatchball(activeLoop, userId);

  return useMemo(() => {
    if (!cycle || !userId) {
      return {
        roles: ['observer' as const],
        primaryRole: 'observer' as const,
        activeSurface: 'strategic' as const,
        isAdmin: false,
        isCycleOwner: false,
      };
    }

    return resolveUserCycleRoles({
      userId,
      workspaceId,
      cycle,
      sessions: cycleSessions,
      orgAware,
    });
  }, [userId, workspaceId, cycle, cycleSessions, orgAware]);
}
